import React, { useEffect, useState } from "react";
import adminApi, { AccountDto } from "../../Api/admin.api";
import { useAuth } from "../../context/AuthContext";
import useMe from "../../hooks/useMe";

/**
 * Bakery selector, shown only to users on an admin role (System Admin, Admin,
 * MES), whichever account they belong to.
 *
 * Switching rewrites the acting account, which every REST call sends as
 * `x-account-id` and the socket sends in its handshake. Hive re-derives the
 * caller's role from the database and ignores the value for anyone who may not
 * switch, so this control is a convenience over a server-side rule rather than
 * the rule itself.
 *
 * It renders nothing for every non-admin role, on any account -- a bakery's
 * sales, warehouse and accounting roles, and REARM staff on Radnik. Those users
 * see only their own tenant and are given no hint that other tenants exist.
 *
 * Two gates have to agree before it renders: `isPlatformAdmin` from the stored
 * session (so there is no flash of a control the user may not use) and
 * `canSwitchTenant` from GET /api/admin/me via useMe (authoritative, and
 * catches a role changed since login). Disagreement, or a failed call, hides
 * the control.
 *
 * A switch reloads the page: device/MES views cache the previous tenant's rows
 * in component state and socket subscriptions, and a full remount is the only
 * way to clear all of it without threading invalidation through every view.
 */
export default function TenantSwitcher() {
  const { isPlatformAdmin, actingAccountId, switchAccount } = useAuth();
  // Shared with the app bar, so a page load makes one /me request.
  const { me, error: meError } = useMe();
  const [accounts, setAccounts] = useState<AccountDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Fail closed: null while unknown, so the control stays hidden until the
  // server has actually confirmed the privilege.
  const serverAllows = meError ? false : me?.canSwitchTenant ?? null;

  useEffect(() => {
    // Only fetch the bakery list once the server has confirmed the privilege,
    // so a non-admin never requests the tenant list at all.
    if (!isPlatformAdmin || serverAllows !== true) return;
    let cancelled = false;

    adminApi
      .getAccounts()
      .then((rows) => {
        if (!cancelled) setAccounts(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Failed to load accounts");
      });

    return () => {
      cancelled = true;
    };
  }, [isPlatformAdmin, serverAllows]);

  if (!isPlatformAdmin || serverAllows !== true) return null;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const raw = e.target.value;
    const next = raw === "" ? null : Number(raw);
    if (next === actingAccountId) return;
    switchAccount(next);
    window.location.reload();
  };

  return (
    <div className="tenant-switcher" title={error ?? "Active bakery"}>
      <select
        className="tenant-switcher-select"
        value={actingAccountId != null ? String(actingAccountId) : ""}
        onChange={handleChange}
        aria-label="Active bakery"
      >
        <option value="">All / none selected</option>
        {accounts.map((account) => (
          <option key={account.id} value={String(account.id)}>
            {account.name ?? `Account ${account.id}`}
          </option>
        ))}
      </select>
    </div>
  );
}
